import type { ComponentChildren } from "preact";
import { Icon } from "./Icon.tsx";
import { cx } from "./lib/cx.ts";

export interface CoachStep { label: string; done: boolean }

/**
 * A lesson's coach: a card that says what to do now while you do it in the
 * page. Unlike a Tour it doesn't drive; the app advances it when the person
 * acts. Rule: one coach at a time, placed by the app over a corner of the area
 * it teaches; `art` is a short illustration band across its top.
 */
export function Coach({ label, eyebrow, title, art, children, steps, actions, class: className }: {
  /** Names the card for assistive tech, such as "Lesson coach". */
  label: string;
  eyebrow?: ComponentChildren; title: ComponentChildren; art?: ComponentChildren;
  /** What to do now. Announced politely as it changes. */
  children: ComponentChildren;
  steps?: ReadonlyArray<CoachStep>;
  actions?: ComponentChildren;
  class?: string;
}) {
  return (
    <aside class={cx("ui-coach", className)} aria-label={label}>
      {art && <div class="ui-coach__art">{art}</div>}
      <div class="ui-coach__body">
        {eyebrow && <p class="ui-coach__eyebrow">{eyebrow}</p>}
        <h2 class="ui-coach__title">{title}</h2>
        <div class="ui-coach__text" aria-live="polite">{children}</div>
        {steps && steps.length > 0 && (
          <ul class="ui-coach__steps">
            {steps.map((step) => (
              <li key={step.label} data-done={step.done ? "" : undefined}>
                <Icon name={step.done ? "check" : "circle"} size={16} />
                <span>{step.label}</span>
                {step.done && <span class="ui-sr-only"> (done)</span>}
              </li>
            ))}
          </ul>
        )}
        {actions && <div class="ui-coach__actions">{actions}</div>}
      </div>
    </aside>
  );
}

/**
 * Points at the one control that matters now: a ring around it and a short
 * label above, such as "Click here". Rule: only while a Coach is asking for
 * that control. Inside the sidebar it marks itself as a tour target, so the
 * icon rail opens to show it.
 */
export function Pointer({ active, label = "Click here", children, class: className }: {
  active: boolean; label?: string; children: ComponentChildren; class?: string;
}) {
  return (
    <div class={cx("ui-pointer", active && "ui-tour-target", className)} data-active={active ? "" : undefined}>
      {active && <span class="ui-pointer__label" role="note">{label}</span>}
      {children}
    </div>
  );
}
